import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlanSelectionCard } from '@/components/plans/PlanSelectionCard';
import { useTranslation } from '@/hooks/useTranslation';
import { plansApi } from '@/api/plans';
import type { SubscriptionPlan } from '@/types/plan';
import logo from '@/assets/images/logo-sidebar.png';

export function PlansPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const fetchedPlans = await plansApi.getAllPlans();
        setPlans(fetchedPlans);
      } catch (error) {
        console.error('Failed to fetch plans:', error);
      } finally {
        setPlansLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const handlePlanSelect = (planCode: string) => {
    if (planCode === 'ENTERPRISE') {
      navigate('/register', { state: { planCode: 'ENTERPRISE', showEnterpriseForm: true } });
    } else {
      navigate('/register', { state: { planCode } });
    }
  };

  return (
    <div 
      className="min-h-screen py-12 px-4 sm:px-6 lg:px-8"
      style={{ backgroundColor: '#F6F6F8' }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header with Logo */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <img 
              src={logo} 
              alt="FeedBatch Logo" 
              style={{ 
                height: '100px',
                width: 'auto'
              }}
            />
          </div>
          <h1 
            className="text-4xl font-bold mb-4"
            style={{ color: '#22212C' }}
          >
            {t('plans.title')}
          </h1>
          <p 
            className="text-xl mb-6"
            style={{ color: 'rgba(50, 71, 92, 0.6)' }}
          >
            {t('plans.selectPlan')}
          </p>
          <div className="flex justify-center gap-4">
            <button
              onClick={() => navigate('/login')}
              style={{ 
                color: 'var(--wl-primary)',
                fontWeight: 500,
                textDecoration: 'none',
                fontSize: '0.875rem',
                background: 'none',
                border: 'none',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = 'var(--wl-accent)'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'var(--wl-primary)'}
            >
              Já tem conta? Faça login
            </button>
          </div>
        </div>

        {/* Plans Grid */}
        {plansLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--wl-primary)]"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan) => (
              <PlanSelectionCard
                key={plan.planCode}
                plan={plan}
                isSelected={false}
                onSelectPlan={handlePlanSelect}
                showCTA={true}
                highlightRecommended={plan.planCode === 'ONE'}
              />
            ))}
          </div>
        )}

        {/* Additional Info */}
        <div className="text-center mt-12">
          <p 
            className="text-sm"
            style={{ color: 'rgba(50, 71, 92, 0.6)' }}
          >
            {t('plans.comparePlans')}
          </p>
        </div>

        {/* Links de Privacidade e Termos */}
        <div className="text-center mt-6 flex justify-center gap-3 text-xs" style={{ color: 'rgba(50, 71, 92, 0.6)' }}>
          <a
            href="/politica-de-privacidade"
            target="_blank"
            rel="noopener noreferrer"
            style={{ 
              color: 'rgba(50, 71, 92, 0.6)',
              textDecoration: 'none'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--wl-accent)'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(50, 71, 92, 0.6)'}
          >
            Política de Privacidade
          </a>
          <span>•</span>
          <a
            href="/termos-de-servico"
            target="_blank"
            rel="noopener noreferrer"
            style={{ 
              color: 'rgba(50, 71, 92, 0.6)',
              textDecoration: 'none'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--wl-accent)'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(50, 71, 92, 0.6)'}
          >
            Termos de Serviço
          </a>
        </div>
      </div>
    </div>
  );
}
